import React from 'react';
import { RadioOption } from '../../types/mmmConfig';

interface RadioGroupProps {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  label,
  value,
  onChange,
  options,
  error,
  helperText,
  required,
  disabled,
  className = '',
}) => {
  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-slate-700 mb-3">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      <div className="space-y-2">
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`
              group flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-slate-300'}
              ${value === opt.value
                ? 'border-violet-500 bg-violet-50/50'
                : 'border-slate-200 bg-white hover:bg-slate-50'
              }
            `}
          >
            <div className="relative mt-0.5">
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={value === opt.value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="sr-only peer"
              />
              <div
                className={`
                  w-5 h-5 rounded-full border-2 transition-all duration-200
                  flex items-center justify-center
                  ${value === opt.value
                    ? 'border-violet-600 bg-violet-600'
                    : 'border-slate-300 bg-white group-hover:border-slate-400'}
                `}
              >
                {value === opt.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${value === opt.value ? 'text-violet-900' : 'text-slate-800'}`}>
                {opt.label}
              </div>
              {opt.description && (
                <div className={`text-xs mt-1 ${value === opt.value ? 'text-violet-700' : 'text-slate-500'}`}>
                  {opt.description}
                </div>
              )}
            </div>
          </label>
        ))}
      </div>
      {error && (
        <p className="text-rose-600 text-xs mt-2 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-rose-500" />
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="text-slate-500 text-xs mt-2">{helperText}</p>
      )}
    </div>
  );
};
